import React, { useState } from 'react'
import {
  Modal,
  TextBody,
  Button,
  ButtonGroup,
  get,
  Box,
  TextHeading,
  Alert,
  Icon,
  useModal,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { muiSuccessFill } from '@ally/metronome-icons'
import { useAccountDetailsContext } from '../../../../services/AccountDetailsProvider'

const StyledBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${get.space('md')};
  margin-bottom: ${get.space('xl')}; ;
`
const ConfirmationWrapper = styled(Box)`
  padding: ${get.space('md')} 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`
const StyledAlert = styled(Alert)`
  div {
    padding: 0;
  }
`
const ModalTrigger: React.FC = () => {
  const { showModal } = useModal()
  return <Button text="Raise Your Rate" onClick={showModal} />
}
const headerRef = React.createRef<HTMLHeadingElement>()
const ConfirmationModalContent: React.FC<{
  setRateIncreaseSuccessful: () => void
}> = ({ setRateIncreaseSuccessful }) => {
  const { closeModal } = useModal()

  const onClose = (): void => {
    setRateIncreaseSuccessful()
    closeModal()
  }

  return (
    <ConfirmationWrapper>
      <Box my="xs" textAlign="center">
        <Icon icon={muiSuccessFill} size="xl" fill="success" />
        <TextHeading tag="h1" size="md" tabIndex={-1} ref={headerRef}>
          You gave yourself a raise. Your new interest and APY will go into
          effect the next business day.
        </TextHeading>
        <TextBody tag="p" size="md">
          We will mail you a confirmation letter with your updated account
          details.
        </TextBody>
        <Button
          data-testid="close-confirmation"
          text="Close"
          variant="primary"
          onClick={onClose}
        />
      </Box>
    </ConfirmationWrapper>
  )
}
const ModalContent: React.FC<{
  currentApyForProduct: number | null
  apy: number | undefined
  availableRateIncreases: number

  setShowConfirmationModal: () => void
}> = ({
  currentApyForProduct,
  apy,
  availableRateIncreases,
  setShowConfirmationModal,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { updateRaiseYourRate } = useAccountDetailsContext()
  const [isRyRUpdateError, setIsRyRUpdateError] = useState(false)
  const { closeModal } = useModal()
  const requestRaiseYourRate = async (): Promise<void> => {
    try {
      setIsLoading(true)
      await updateRaiseYourRate()
      setShowConfirmationModal()
    } catch (error) {
      setIsRyRUpdateError(true)
    } finally {
      setIsLoading(false)
    }
  }
  return (
    <Box m="md">
      <StyledBox>
        <TextHeading
          tag="h1"
          size="sm"
          tabIndex={-1}
          data-testId="ryr-modal-title"
          ref={headerRef}
        >
          Raise Your Rate
        </TextHeading>
        {isRyRUpdateError && (
          <StyledAlert
            disableContainer
            focusOnMount
            variant="error"
            heading=""
            body="We're unable to process your request at this time. Please try again later"
          />
        )}
        <TextBody tag="p" size="md">
          Our annual percentage yield for your term and balance tier is{' '}
          <strong>{currentApyForProduct}%</strong>. Yours is{' '}
          <strong>{apy}%</strong>. You have {availableRateIncreases} eligible
          rate increase(s) remaining for this CD.
        </TextBody>
        <TextBody tag="p" size="md">
          Keep in mind, if the APY for this account changes on the day you raise
          your rate, you&apos;ll automatically get the highest rate we
          advertised that day.
        </TextBody>
        <TextBody tag="p" size="md">
          Are you ready to raise your rate?
        </TextBody>
      </StyledBox>
      <ButtonGroup>
        <Button
          text="Yes, Raise It"
          onClick={requestRaiseYourRate}
          disabled={isLoading}
        />
        {!isLoading && <Button text="Not Now" onClick={closeModal} />}
      </ButtonGroup>
    </Box>
  )
}
export const RyrModal: React.FC<{
  currentApyForProduct: number | null
  apy: number | undefined
  availableRateIncreases: number
  setRateIncreaseSuccessful: React.Dispatch<React.SetStateAction<boolean>>
}> = ({
  currentApyForProduct,
  availableRateIncreases,
  apy,
  setRateIncreaseSuccessful,
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  return (
    <Modal
      ref={headerRef}
      content={
        <>
          {showConfirmationModal ? (
            <ConfirmationModalContent
              setRateIncreaseSuccessful={(): void =>
                setRateIncreaseSuccessful(true)
              }
            />
          ) : (
            <ModalContent
              currentApyForProduct={currentApyForProduct}
              apy={apy}
              availableRateIncreases={availableRateIncreases}
              setShowConfirmationModal={(): void =>
                setShowConfirmationModal(true)
              }
            />
          )}
        </>
      }
      width="lg"
      trigger={<ModalTrigger />}
    />
  )
}
